import * as Headless from "@headlessui/react";
import clsx from "clsx";

import { LoadingSpinner } from "../LoadingSpinner";

import "./styles.css";

const colors = {
  primary: [
    "[--switch-bg-ring:theme(colors.base-input)]",
    "[--switch-shadow:theme(colors.brand-tertiary)]",
    "[--switch-bg:#FD701440]",
    "[--switch:theme(colors.brand-accent)]",
  ],
};

type Color = keyof typeof colors;

const SwitchSide = ({
  text,
  loading,
  offSide,
}: {
  text: string;
  loading?: boolean;
  offSide?: boolean;
}) => (
  <div
    className={clsx("pointer-events-none relative inline-block px-1 py-[.375rem]", {
      "opacity-20": loading,
    })}
  >
    <span
      className={clsx(
        "switch-text overflow-hidden text-ellipsis text-nowrap",
        // Checked
        offSide
          ? "group-data-[checked]:font-normal group-data-[checked]:text-base-black"
          : "group-data-[checked]:font-bold group-data-[checked]:text-[--switch]",
        {
          // Unchecked
          "font-bold text-[--switch]": offSide,
        },
      )}
    >
      {text}
    </span>

    <div
      className={clsx(
        "absolute left-0 top-0 h-full w-full rounded-full",
        // Transition
        "transition duration-200 ease-in-out",
        // Unchecked
        offSide ? "translate-x-0 bg-[--switch-bg]" : "translate-x-full bg-transparent",
        // Checked
        offSide
          ? "group-data-[checked]:translate-x-[-100%] group-data-[checked]:bg-transparent"
          : "group-data-[checked]:bg-[--switch-bg] group-data-[checked]:font-bold " +
              "group-data-[checked]:translate-x-0 group-data-[checked]:text-[--switch]",
      )}
    />
  </div>
);

export const Switch = ({
  color = "primary",
  offText,
  onText,
  className,
  loading,
  disabled,
  ...props
}: {
  offText: string;
  onText: string;
  color?: Color;
  className?: string;
  loading?: boolean;
} & Omit<Headless.SwitchProps, "as" | "className" | "children">) => {
  return (
    <Headless.Switch
      data-slot="control"
      {...props}
      disabled={disabled || loading}
      className={clsx(
        "system-switch",
        className,
        offText ? "" : "w-10 sm:w-8",
        "group relative isolate inline-flex h-6 items-center rounded-full px-1",
        "bg-base-white outline outline-2 outline-base-input",
        // Transitions
        "transition duration-0 ease-in-out data-[changing]:duration-200",
        // Focus
        "focus-visible:outline-base-black",
        // Hover
        "data-[hover]:outline-base-black",
        { relative: loading },
        colors[color],
      )}
    >
      {loading && (
        <LoadingSpinner
          size="1rem"
          className="absolute left-[calc(50%_-_.5rem)] top-[calc(50%_-_.5rem)] text-[--switch]"
        />
      )}

      <SwitchSide text={onText} loading={loading} />

      <SwitchSide text={offText} loading={loading} offSide />
    </Headless.Switch>
  );
};
